import React from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons';
import AssociatedContactSelector from '../../../components/tasks/associated_contact_selector';
import PersonContactForm from '../person_contact_form';
import CompanyContactForm from '../company_contact_form';
import LeadFormFields from '../lead_form_fields';
import Modal from 'shared/modal';
import Button from 'shared/button';
import { addNumber, changePrimary, deleteNumber, phoneNumberChange } from '../helper/person_phone_number';
import { RADIO_OPTIONS } from './helper';

export default function FormDisplay({
  allLeadReferralSources,
  company,
  contactIsPerson,
  databaseContact,
  docAccessLevels,
  fetchingContact,
  handleSave,
  hasDatabases,
  hideLeadModal,
  lead,
  leadData,
  leaseChecked,
  leadStatuses,
  listings,
  person,
  persistAddress,
  project,
  radioSelected,
  requestSending,
  saleChecked,
  setCompany,
  setDatabaseContact,
  setLeadData,
  setPerson,
  setPersistAddress,
  useVaultGates,
  valid
}) {
  const areFieldsRequired = !lead;

  function addressCompanyFieldChange(newValue, field) {
    setCompany({ ...company, address: { ...company?.address, [field]: newValue } });
  }

  function basicCompanyFieldChange(newValue, field) {
    setCompany({ ...company, [field]: newValue });
  }

  function basicPersonFieldChange(newValue, field) {
    setPerson({ ...person, [field]: newValue });
  }

  function basicLeadFieldChange(newValue, field) {
    setLeadData(prevData => ({ ...prevData, [field]: newValue }));
  }

  function phoneNumberPersonFieldChange(id, newValue, field) {
    setPerson({ ...person, phoneNumbers: phoneNumberChange(id, newValue, field, person.phoneNumbers) });
  }

  function addPhoneNumber() {
    setPerson({ ...person, phoneNumbers: addNumber(person.phoneNumbers) });
  }

  function changePhoneNumberPrimary(id, makePrimary) {
    setPerson({ ...person, phoneNumbers: changePrimary(id, makePrimary, person.phoneNumbers) });
  }

  function deletePhoneNumber(id) {
    setPerson({ ...person, phoneNumbers: deleteNumber(id, person.phoneNumbers) });
  }

  function modelAdded(contact) {
    setDatabaseContact(contact);
  }

  let contactForm = '';
  switch (radioSelected) {
  case RADIO_OPTIONS.NEW_PERSON:
    contactForm = 'person';
    break;
  case RADIO_OPTIONS.NEW_COMPANY:
    contactForm = 'company';
    break;
  case RADIO_OPTIONS.SEARCH_CONTACTS:
    contactForm = 'search';
    break;
  }
  if (lead) contactForm = contactIsPerson ? 'person' : 'company';

  return (
    <form onSubmit={handleSave}>
      {fetchingContact ? (
        <div className="d-flex justify-content-center">
          <FontAwesomeIcon icon={faSpinner} size="2x" spin/>
        </div>
      ) : (
        <React.Fragment>
          {contactForm === 'search' && (
            <div name="select-contact-form">
              <label>Contact</label>
              <AssociatedContactSelector
                className="mb-4"
                contacts={project.contacts}
                localSearch={hasDatabases}
                selectedValue={databaseContact}
                onModelAdded={modelAdded}
              />
            </div>
          )}
          {contactForm === 'person' && (
            <PersonContactForm
              accessibleDatabaseContacts={lead?.accessibleDatabaseContacts}
              areFieldsRequired={areFieldsRequired}
              displayName={lead?.displayName}
              person={person}
              onAddPhoneNumber={addPhoneNumber}
              onChangePhoneNumberPrimary={changePhoneNumberPrimary}
              onDeletePhoneNumber={deletePhoneNumber}
              onFieldChange={basicPersonFieldChange}
              onPhoneNumberChange={phoneNumberPersonFieldChange}
            />
          )}
          {contactForm === 'company' && (
            <CompanyContactForm
              accessibleDatabaseContacts={lead?.accessibleDatabaseContacts}
              areFieldsRequired={areFieldsRequired}
              company={company}
              displayName={lead?.displayName}
              persistAddress={persistAddress}
              onAddressChange={addressCompanyFieldChange}
              onFieldChange={basicCompanyFieldChange}
              onToggleAddressFields={() => setPersistAddress(!persistAddress)}
            />
          )}
        </React.Fragment>
      )}
      <LeadFormFields
        docAccessLevels={docAccessLevels}
        lead={lead}
        leadData={leadData}
        leadReferralSources={allLeadReferralSources}
        leadStatuses={leadStatuses}
        leaseChecked={leaseChecked}
        listings={listings}
        project={project}
        saleChecked={saleChecked}
        useVaultGates={useVaultGates}
        onFieldChange={basicLeadFieldChange}
      />

      <Modal.Footer footerOrientation="right" onHide={() => hideLeadModal()}>
        <Button.Cancel
          className="mr-3"
          name="cancel"
          type="button"
          onClick={() => hideLeadModal()}
        >
          Cancel
        </Button.Cancel>
        <Button.Primary disabled={!valid || requestSending} name="save">
          Save
        </Button.Primary>
      </Modal.Footer>
    </form>
  );
}

FormDisplay.propTypes = {
  allLeadReferralSources: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    label: PropTypes.string.isRequired
  })).isRequired,
  company: PropTypes.object,
  contactIsPerson: PropTypes.bool.isRequired,
  databaseContact: PropTypes.object,
  docAccessLevels: PropTypes.array,
  fetchingContact: PropTypes.bool.isRequired,
  handleSave: PropTypes.func.isRequired,
  hasDatabases: PropTypes.bool.isRequired,
  hideLeadModal: PropTypes.func.isRequired,
  lead: PropTypes.object,
  leadData: PropTypes.object,
  leadStatuses: PropTypes.array,
  leaseChecked: PropTypes.bool.isRequired,
  listings: PropTypes.array,
  persistAddress: PropTypes.bool.isRequired,
  person: PropTypes.object,
  project: PropTypes.object,
  radioSelected: PropTypes.string.isRequired,
  requestSending: PropTypes.bool,
  saleChecked: PropTypes.bool.isRequired,
  setCompany: PropTypes.func.isRequired,
  setDatabaseContact: PropTypes.func.isRequired,
  setLeadData: PropTypes.func.isRequired,
  setPersistAddress: PropTypes.func.isRequired,
  setPerson: PropTypes.func.isRequired,
  useVaultGates: PropTypes.bool.isRequired,
  valid: PropTypes.bool.isRequired
};
